/**
 * AdminWhatsAppTemplates.jsx · Editor de plantillas WhatsApp del Cerebro de Agentes.
 * ─────────────────────────────────────────────────────────────────────────────
 * Permite listar, editar y previsualizar plantillas usadas por A2/A4 (mock actual).
 * Soporta variables tipo {{nombre}}, {{fecha}}, {{hora}}, {{codigo_ot}}, {{tecnico}}.
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import WhatsAppTabs from "../components/WhatsAppTabs";   // v101.192.0
import api from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Save, Eye, Power, Loader2, ChevronRight } from "lucide-react";

const VAR_HINTS = {
  a2_proposal:            ["nombre", "fecha", "hora", "codigo_ot", "tecnico"],
  a2_alternatives:        ["nombre", "codigo_ot", "opciones"],
  a2_confirmed:           ["nombre", "fecha", "hora", "codigo_ot", "tecnico"],
  a2_technician_notice:   ["codigo_ot", "nombre", "fecha", "hora", "direccion"],
  a2_manual_review:       ["codigo_ot", "motivo", "nombre", "telefono"],
  a4_route:               ["fecha", "total_ots", "km", "lista_ots", "tecnico"],
  a4_delay:               ["nombre", "minutos", "codigo_ot"],
};

const SAMPLE_VALUES = {
  nombre:      "María López",
  fecha:       "Jueves 22 de Agosto",
  hora:        "10:00",
  codigo_ot:   "OT-00123",
  tecnico:     "Juan Pérez",
  opciones:    "  *A*: Jueves 09:00\n  *B*: Jueves 14:00\n  *C*: Viernes 10:00",
  direccion:   "Av. Amazonas N24-155 y Colón",
  motivo:      "Timeout · sin respuesta del cliente en 2h",
  telefono:    "+593 99 123 4567",
  minutos:     "45",
  total_ots:   "5",
  km:          "23.4",
  lista_ots:   "1. 09:00 OT-00123 · UPC Portoviejo\n2. 10:30 OT-00124 · Juan Vera",
};

export default function AdminWhatsAppTemplates() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", body: "", active: true, variables: [] });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/templates");
      setItems(data.items || []);
      if (!selected && data.items?.length) selectTemplate(data.items[0]);
    } catch (e) {
      toast.error("Error cargando plantillas");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectTemplate = (t) => {
    setSelected(t);
    setForm({
      name:      t.name || "",
      body:      t.body || "",
      active:    t.active !== false,
      variables: Array.isArray(t.variables) ? t.variables : (VAR_HINTS[t.key] || []),
    });
    renderPreview(t.body, t.key);
  };

  const renderPreview = async (body, key) => {
    if (!key) return;
    const vars = Object.fromEntries((VAR_HINTS[key] || []).map((v) => [v, SAMPLE_VALUES[v] || `<${v}>`]));
    try {
      const { data } = await api.post(`/whatsapp/templates/${key}/preview`, vars);
      setPreview(data.rendered);
    } catch {
      // Local render fallback
      const rendered = (body || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => vars[k] || "—");
      setPreview(rendered);
    }
  };

  useEffect(() => {
    if (selected?.key) renderPreview(form.body, selected.key);
    // eslint-disable-next-line
  }, [form.body, selected?.key]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/whatsapp/templates/${selected.key}`, {
        name: form.name,
        body: form.body,
        active: form.active,
        variables: form.variables,
      });
      toast.success("Plantilla actualizada");
      setSelected(data);
      // Refrescar lista
      const list = await api.get("/whatsapp/templates");
      setItems(list.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const insertVar = (v) => {
    const tag = `{{${v}}}`;
    setForm((f) => ({ ...f, body: (f.body || "") + tag }));
  };

  const varsForCurrent = useMemo(
    () => (selected ? (VAR_HINTS[selected.key] || form.variables) : []),
    [selected, form.variables],
  );

  return (
    <Layout>
      <div className="space-y-4" data-testid="wa-templates-page">
        <WhatsAppTabs />
        {/* Header */}
        <div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
               style={{ background: "#F3E8FF", border: "1px solid #A855F7" }}>
            <MessageSquare size={11} className="text-purple-700" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-purple-700">
              Cerebro de Agentes · WhatsApp Mock
            </span>
          </div>
          <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Plantillas de WhatsApp</h1>
          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
            Edita el tono y contenido de los mensajes que A2 y A4 envían al cliente y al técnico.
            Las variables {"{{nombre}}"}, {"{{fecha}}"}, {"{{hora}}"} se reemplazan al enviar el mensaje.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_360px] gap-4">
          {/* Lista */}
          <div className="neu-card overflow-hidden">
            <div className="p-3 border-b bg-gray-50 text-[10px] uppercase tracking-widest font-bold text-gray-500">
              {items.length} plantillas
            </div>
            {loading ? (
              <div className="p-6 text-center text-xs text-gray-400"><Loader2 size={16} className="animate-spin mx-auto mb-1"/>Cargando...</div>
            ) : (
              <div className="max-h-[560px] overflow-y-auto">
                {items.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => selectTemplate(t)}
                    data-testid={`tpl-item-${t.key}`}
                    className={`w-full text-left px-3 py-2.5 border-b hover:bg-purple-50 transition ${selected?.key === t.key ? "bg-purple-50" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-purple-700 truncate">{t.key}</div>
                        <div className="text-xs font-semibold truncate" style={{ color: "#0A3747" }}>{t.name}</div>
                      </div>
                      {!t.active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-mono">OFF</span>}
                      {selected?.key === t.key && <ChevronRight size={14} className="text-purple-700 flex-shrink-0"/>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Editor */}
          <div className="neu-card p-4 space-y-3">
            {!selected ? (
              <div className="p-6 text-center text-gray-400 text-sm">Selecciona una plantilla</div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700">{selected.key}</div>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="text-lg font-bold border-b border-transparent hover:border-gray-300 focus:border-purple-500 focus:outline-none w-full mt-0.5"
                      style={{ color: "#0A3747" }}
                      data-testid="tpl-name-input"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      data-testid="tpl-active"
                    />
                    <Power size={12} className={form.active ? "text-emerald-600" : "text-gray-400"}/>
                    {form.active ? "Activa" : "Desactivada"}
                  </label>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Cuerpo del mensaje</label>
                  <textarea
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    rows={10}
                    className="w-full p-3 border rounded-lg text-sm font-mono resize-y focus:border-purple-500 focus:outline-none"
                    data-testid="tpl-body-input"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Insertar variable</label>
                  <div className="flex flex-wrap gap-1">
                    {varsForCurrent.map((v) => (
                      <button
                        key={v}
                        onClick={() => insertVar(v)}
                        data-testid={`insert-var-${v}`}
                        className="px-2 py-1 text-[11px] font-mono bg-purple-100 hover:bg-purple-200 rounded text-purple-800 transition"
                      >
                        + {"{{"}{v}{"}}"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-2 border-t border-gray-100">
                  <button
                    onClick={save}
                    disabled={saving}
                    className="neu-btn neu-btn-primary"
                    data-testid="tpl-save"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Guardar
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Preview */}
          <div className="neu-card overflow-hidden">
            <div className="p-3 border-b bg-emerald-50 flex items-center gap-2">
              <Eye size={12} className="text-emerald-700"/>
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-800">Vista previa · WhatsApp</span>
            </div>
            <div className="p-4" style={{ background: "#E5DDD5", minHeight: "500px" }}>
              <div className="max-w-full">
                <div
                  className="bg-white rounded-lg shadow-sm p-3 relative"
                  style={{
                    borderTopLeftRadius: "12px", borderBottomLeftRadius: "12px",
                    borderTopRightRadius: "12px", borderBottomRightRadius: "2px",
                  }}
                  data-testid="tpl-preview"
                >
                  <div className="text-xs whitespace-pre-wrap" style={{ color: "#111", lineHeight: 1.5 }}>
                    {preview || "…"}
                  </div>
                  <div className="text-right mt-1">
                    <span className="text-[9px] text-gray-400">✓✓ 10:24</span>
                  </div>
                </div>
                <div className="mt-2 text-[9px] text-gray-500 text-center">
                  Sample: nombre=&quot;{SAMPLE_VALUES.nombre}&quot; · fecha=&quot;{SAMPLE_VALUES.fecha}&quot;
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
